<template>
  <div class="menu-options-modal" tabindex="0" ref="menu-option-modal">
    <div class="menu-header">
      <div class="heading-container">
        <span
          v-if="showLanguageMenu"
          class="back_icon"
          @click.stop="handleBackIcon"
        >
          <NavigateBack :fillColor="primaryThemeColor"></NavigateBack>
        </span>
        <span v-if="showLanguageMenu" class="item_icon">
          <component :is="propsForOptionItems.icon"> </component>
        </span>
        <div id="menu-heading" class="heading" v-t="menuHeading"></div>
      </div>
      <button
        class="tab-focused-btn"
        aria-label="close menu"
        @click.stop="handleCloseDrawer()"
      >
        <div class="close-modal">
          <BaseIcon name="closeIcon" />
        </div>
      </button>
    </div>
    <ul
      class="menu-options"
      v-if="showLanguageMenu"
      aria-labelledby="menu-heading"
    >
      <li
        :key="item.label"
        class="language-item"
        v-for="item in getOptionItems"
        @click.stop="handleOptionItemClick(item)"
        :class="{ active: isConditionMatched(item) }"
      >
        <span
          class="dot"
          :class="isConditionMatched(item) ? 'active_dot' : 'inactive_dot'"
        ></span>
        <span class="language-label">{{ item.label }}</span>
      </li>
    </ul>
    <ul v-else class="menu-options" aria-labelledby="menu-heading">
      <li
        role="menuitem"
        class="menu-item"
        v-if="multilingualEnabled"
        :data-testId="templateStrings.CHANGE_LANGUAGE"
      >
        <button
          class="tab-focused-btn wd100 change_language_row"
          @click.stop="handleShowLanguageMenu"
        >
          <div class="list-item">
            <GlobeIcon
              class="label-icon change-language-globe-icon"
            ></GlobeIcon>
            <p v-t="templateStrings.CHANGE_LANGUAGE"></p>
          </div>
          <NavigateNext
            fill="#2C2C2C"
            class="navigate_next_icon"
          ></NavigateNext>
        </button>
      </li>
      <li
        class="menu-item"
        v-if="showSound"
        aria-checked="true"
        role="menuitem switch"
        :data-testId="templateStrings.SOUND"
      >
        <button class="tab-focused-btn wd100" @click.stop="changeSoundState">
          <div class="list-item">
            <NotificationIcon
              class="label-icon sound-notification-icon"
            ></NotificationIcon>
            <div class="switch-container">
              <p v-t="templateStrings.SOUND"></p>
              <button class="tab-focused-btn" @click.stop="changeSoundState">
                <CustomSwitch v-model="sound" ref="custom-switch" />
              </button>
            </div>
          </div>
        </button>
      </li>
      <li
        role="menuitem"
        class="menu-item"
        v-if="showResetChat && hasChatsInitiated"
        :data-testId="templateStrings.RESTART_CHAT_LABEL"
      >
        <button class="tab-focused-btn wd100" @click.stop="handleResetChat">
          <div class="list-item">
            <ResetIcon class="label-icon reset-chat-icon"></ResetIcon>
            <p
              v-t="templateStrings.RESTART_CHAT_LABEL"
              class="reset-chat-label"
            ></p>
          </div>
        </button>
      </li>
    </ul>
  </div>
</template>

<script>
import {
  notificationConstants,
  constantStrings,
  jsonConfigTemplate,
  chatEvents
} from '@/services/constants'
import { getTranslation, isTypeBOT } from '@/services/utils'
import notification from '@/services/notification'
import CustomSwitch from '../components/CustomSwitch.vue'
import OptionItemJsonConfig from '@/services/optionItemJsonConfig'

import BaseIcon from '@/views/BaseIcon.vue'
import NotificationIcon from '@/views/svgs-icons/NotificationIcon.vue'
import ResetIcon from '@/views/svgs-icons/ResetIcon.vue'
import NavigateNext from '@/views/svgs-icons/NavigateNext.vue'
import GlobeIcon from '@/views/svgs-icons/GlobeIcon.vue'
import ExitIcon from '@/views/svgs-icons/ExitIcon.vue'
import NavigateBack from '@/views/svgs-icons/NavigateBack.vue'

export default {
  components: {
    BaseIcon,
    NotificationIcon,
    CustomSwitch,
    NavigateNext,
    GlobeIcon,
    ExitIcon,
    ResetIcon,
    NavigateBack
  },
  data() {
    return {
      showLanguageMenu: false,
      propsForOptionItems: OptionItemJsonConfig.DEFAULT_CONFIG
    }
  },
  mounted() {
    this.$refs['menu-option-modal']?.focus()
  },
  watch: {
    sound: {
      handler(value) {
        localStorage.setItem('sound', value)
      },
      immediate: true
    },

    defaultSound: {
      handler(value) {
        this.$store.dispatch('updateNotificationSound', value)
      },
      immediate: true
    }
  },
  computed: {
    sound: {
      get: function () {
        return this.$store.state.notificationSound
      },
      set: function (value) {
        if (value) {
          notification.play(notificationConstants.TOGGLE)
        }
        this.$store.dispatch('updateNotificationSound', value)
      }
    },
    getOptionItems() {
      return this.propsForOptionItems?.options() || []
    },
    showResetChat() {
      const {
        isEnabled,
        placement = constantStrings.RESTART_CHAT_PLACEMENT_INPUT_FIELD
      } = this.$store.state.widgetConfig?.restartChat || {}
      return (
        isEnabled &&
        placement === constantStrings.RESTART_CHAT_PLACEMENT_MENU &&
        isTypeBOT(this.$store.state.controlType)
      )
    },
    showSound() {
      return this.$store.getters.isSoundEnable || true
    },
    defaultSound() {
      if (this.showSound) {
        const localSoundValue = localStorage.getItem('sound')
        return localSoundValue
          ? localSoundValue === 'true'
          : this.$store.getters.isSoundOnByDefault
      }
      return false
    },
    templateStrings() {
      return {
        END_CHAT: jsonConfigTemplate.END_CHAT_BUTTON_CTA,
        SOUND: constantStrings.SOUND,
        CHANGE_LANGUAGE: `${getTranslation(
          constantStrings.LANGUAGE
        )} - ${this.$i18n.locale?.toUpperCase()}`,
        RESTART_CHAT_LABEL: constantStrings.RESTART_PRIMARY_BTN_TEXT
      }
    },
    menuHeading() {
      if (this.showLanguageMenu) {
        return this.propsForOptionItems.title()
      }
      return 'Settings'
    },
    closeIcon() {
      return `${this.$cdn}/images/close_icon.svg`
    },
    multilingualEnabled() {
      return this.$store.state.widgetConfig?.multilingual?.enabled || false
    },
    primaryThemeColor() {
      return this.$store.state.widgetConfig?.theme?.color
    },
    isSendTranscriptEnabled() {
      return this.$store.state.widgetConfig.endChat
        ? this.$store.state.widgetConfig?.endChat?.isSendTranscriptEnabled
        : true
    },
    hasChatsInitiated() {
      return (this.$store.state.chats || []).some(
        (chat) => chat?.content?.message !== constantStrings.RESTART_CHAT_LABEL
      )
    }
  },
  methods: {
    handleCloseDrawer() {
      this.$store.dispatch('toggleShowHeaderDrawer')
    },
    changeSoundState() {
      this.$refs['custom-switch'].toggle()
    },
    async handleResetChat() {
      const { isEnabled } =
        this.$store.state.widgetConfig?.restartChat?.showWarning || {}
      if (isEnabled) {
        this.$store.dispatch('updateEndChatModalOnChatRestart', true)
        this.$store.dispatch('updateEndChatModalStatus', true)
      } else {
        await this.$store.dispatch('endChat', {
          message: constantStrings.RESTART_CHAT_LABEL,
          showSessionRestartGreeting: true,
          hideEndChatGreeting: this.isSendTranscriptEnabled,
          restartChat: true,
          clearConversation: true,
          hidePill: true
        })

        const eventData = {
          eventType: chatEvents.WIDGET_EVENT,
          subType: constantStrings.CHAT_RESTART
        }
        this.$store.dispatch('sendEvent', { eventData })
        this.$store.dispatch('toggleShowHeaderDrawer')
      }
    },
    handleShowLanguageMenu() {
      this.showLanguageMenu = !this.showLanguageMenu
      this.propsForOptionItems = OptionItemJsonConfig.LANGUAGE_CONFIG
    },
    handleOptionItemClick(item) {
      this.propsForOptionItems.clickHandler(item)
      this.showLanguageMenu = !this.showLanguageMenu
    },
    isConditionMatched(item) {
      return (
        this.propsForOptionItems.activeClassConditions.enabled &&
        this.propsForOptionItems.activeClassConditions.condition(item)
      )
    },
    handleBackIcon() {
      this.showLanguageMenu = !this.showLanguageMenu
      this.$store.dispatch('toggleShowHeaderDrawer')
    }
  }
}
</script>

<style lang="scss" scoped>
@import '@/styles/css/theme.scss';
@import '@/styles/css/commonStyles.scss';

.menu-options-modal {
  background: #ffffff;
  border: 1px solid rgba(0, 0, 0, 0.11);
  box-shadow: 0px 4px 12px rgba(0, 0, 0, 0.2);
  @include border-radius(var(--radius-chat-inner-component));
  max-height: 350px;
  padding: 0px 24px 24px 24px;
  * {
    box-sizing: border-box;
  }
  .menu-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 16px 0px;
    .heading {
      font-size: 20px;
      font-weight: 500;
      line-height: normal;
      color: var(--Standard-Grey-000000---Black, #000);
    }
    .close-modal {
      cursor: pointer;
    }
    .heading-container {
      display: flex;
      align-items: center;
      .item_icon {
        margin-right: 8px;
      }
      .back_icon {
        cursor: pointer;
        margin-left: -10px;
      }
    }
  }
  .menu-options {
    overflow-y: auto;
    max-height: 305px;
    padding-left: 0;
    margin: 0;
    list-style: none;
    .menu-option {
      list-style: none;
      display: flex;
      flex-direction: row;
      align-items: center;
      padding: 10px 8px 10px 16px;
      width: 100%;
      height: 40px;
      background: #ffffff;
      font-style: normal;
      font-size: 16px;
      font-weight: 400;
      line-height: 20px;
      color: var(--widget-primary-color);
      cursor: pointer;
      user-select: none;
      border-bottom: 1px solid rgba(0, 0, 0, 0.1);
      box-sizing: border-box;
      &.knowledgeAI-menu-option {
        font-size: 16px;
        line-height: 20px;
        color: #000000;
      }
    }
    .language-item {
      display: flex;
      align-items: center;
      padding: 16px 0;
      border-bottom: 1px solid rgba(0, 0, 0, 0.1);
      font-size: 16px;
      cursor: pointer;
      &:last-child {
        border-bottom: none;
      }
      .dot {
        width: 9px;
        height: 9px;
        border-radius: 50%;
      }
      .active_dot {
        background: var(--widget-primary-color);
      }
      .inactive_dot {
        background: transparent;
      }
      .language-label {
        margin-left: 16px;
      }
    }
    .menu-item {
      padding: 16px 0;
      cursor: pointer;
      border-bottom: 1px solid rgba(0, 0, 0, 0.1);
      &:last-child {
        border-bottom: none;
      }
    }
    .tab-focused-btn {
      display: flex;
      align-items: center;
      justify-content: space-between;
      cursor: pointer;
      .list-item {
        width: 100%;
        display: flex;
        align-items: center;
        p {
          font-size: 16px;
        }
        .label-icon {
          margin-right: 1rem;
          &.sound-notification-icon {
            margin-left: -4px;
          }
        }
        .switch-container {
          width: 100%;
          display: flex;
          align-items: center;
          justify-content: space-between;
        }
      }
      .navigate_next_icon {
        margin-right: 4px;
      }
    }
    .menu-option:hover {
      background: rgba(0, 0, 0, 0.06);
    }
  }

  @media screen and (min-width: 400px) {
    width: 60%;
    margin: 0 auto;
  }
}
</style>
